import React from 'react';
import { Block, BLOCKS, INLINES, Inline } from '@contentful/rich-text-types';
import { RenderNode } from '@contentful/rich-text-react-renderer';
import { Asset } from 'contentful';

import { IHomepageHeroFields } from '../../../types/contentful';

import { CFColorTokenProp } from '../../theme/tokens/contentful-color';

import { getImageData } from '../../utils/contentful';

import { Text } from '../Text';
import { Box } from '../Box';
import { Flexbox } from '../Flexbox';
import { PopUp } from './PopUp';
import { NoSpamText } from './NoSpamText';
import { Layout, Column, BlockContainer } from '../Layout';
import { Eyebrow } from '../Eyebrow';
import RichText from '../RichText';
import { EmbeddedInlineEntryNode } from '../RichText/Nodes';
import CtaWidget from '../CtaWidget';
import {
  ctaWidgetEnum,
  DEFAULT_LABEL,
  DEFAULT_BUTTON_TEXT
} from '../CtaWidget/shared';

import {
  CTA,
  CTAText,
  ImageContainer,
  RelativeBox,
  DesktopImage,
  MobileVersion,
  DesktopBlockContainer,
  HomepagePhoneNumberWrapper,
  SubheadText,
  InlineAnchor,
  ParagraphWithLineBreaks,
  MobileImage,
  BackgroundImage
} from './styles';

export function HomepageHero({
  headingText,
  useLightHeadingText,
  subheadingText,
  useLightSubheadingText,
  mobileSubheadingText,
  mobileHeadingText,
  image,
  desktopImage,
  fullWidthDesktopImage,
  backgroundColor,
  desktopBackgroundColor,
  desktopSubheadingSize,
  mobileSubheadingSize,
  ctaText,
  ctaColor,
  ctaHoverColor,
  ctaDesktopColor,
  ctaDesktopHoverColor,
  eyebrow,
  privacyPolicyDisclaimer
}: IHomepageHeroFields) {
  const RICH_TEXT_OVERRIDE: RenderNode = {
    [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => {
      return (
        <ParagraphWithLineBreaks
          as="p"
          fontSize={{
            0: '2xs',
            tablet: 's'
          }}
          fontWeight="400"
          colour={{
            0: 'defaultText',
            desktop: useLightHeadingText ? 'white' : 'defaultText'
          }}
          textAlign="center"
        >
          {children}
        </ParagraphWithLineBreaks>
      );
    },
    [INLINES.HYPERLINK]: (node, children) => (
      <InlineAnchor
        href={node.data.uri}
        useLightText={useLightHeadingText || false}
      >
        {children}
      </InlineAnchor>
    ),
    [INLINES.EMBEDDED_ENTRY]: (node, children) => (
      <EmbeddedInlineEntryNode
        node={node}
        colour={{
          0: 'defaultText',
          desktop: useLightHeadingText ? 'white' : 'defaultText'
        }}
        fontWeight="p"
      >
        {children}
      </EmbeddedInlineEntryNode>
    )
  };

  const DesktopHeroContents = () => {
    return (
      <Column size={10}>
        <Flexbox
          flexDirection="column"
          justifyContent="center"
          flex="0 0 28rem"
        >
          {eyebrow && (
            <Box marginBottom="m" marginTop="2xl">
              <Column size={{ 0: 11, tablet: 8 }}>
                <Eyebrow eyebrowFields={eyebrow.fields} />
              </Column>
            </Box>
          )}
          <Box marginBottom="m" marginTop={eyebrow ? 'm' : '3xl'}>
            <Text
              as="h1"
              fontWeight="h1"
              fontSize={{ 0: 'h1_tablet' }}
              colour={useLightHeadingText ? 'white' : 'defaultText'}
              textAlign="left"
              data-elementid="homepage-hero-headline"
              data-testid="homepage-hero-headline"
              dangerouslySetInnerHTML={{
                __html: headingText || ''
              }}
            />
          </Box>
          <Box marginBottom="ml">
            <SubheadText
              as="h2"
              fontWeight="p"
              desktopSubheadingSize={desktopSubheadingSize || '1.75'}
              colour={useLightSubheadingText ? 'white' : 'defaultText'}
              textAlign="left"
              data-elementid="homepage-hero-subhead"
            >
              {subheadingText}
            </SubheadText>
          </Box>
          <Box marginBottom="3xl">
            <Flexbox flexDirection="column">
              <HomepagePhoneNumberWrapper
                data-elementid="phone-number-wrapper-desktop"
                style={{ flexFlow: 'wrap' }}
              >
                <CtaWidget
                  id="homepage-hero-phone-number-desktop"
                  module="Hero"
                  errorPosition="bottom"
                  width="100%"
                  sysId="legacyCta"
                  designType={ctaWidgetEnum.MOBILE_PHONE_NUMBER}
                  placeholder={DEFAULT_LABEL}
                  label={DEFAULT_LABEL}
                  cfButtonColor={
                    (ctaDesktopColor as CFColorTokenProp) || 'green'
                  }
                  cfButtonHoverColor={
                    (ctaDesktopHoverColor as CFColorTokenProp) || 'sweetmint'
                  }
                  buttonContent={ctaText || DEFAULT_BUTTON_TEXT}
                  data-testid="homepage-hero-phone-number-desktop"
                />
              </HomepagePhoneNumberWrapper>
              {privacyPolicyDisclaimer && (
                <Box marginTop="ml">
                  <RichText
                    data={privacyPolicyDisclaimer}
                    overrideOptions={RICH_TEXT_OVERRIDE}
                  />
                </Box>
              )}
              <PopUp />
            </Flexbox>
            <NoSpamText />
          </Box>
        </Flexbox>
        <Box flex="1" paddingTop="m">
          <DesktopImage
            {...getImageData((desktopImage as Asset) || {})}
            visibility={fullWidthDesktopImage?.fields ? 'hidden' : 'visible'}
            loading="eager"
            heroImage={true}
          />
        </Box>
      </Column>
    );
  };

  return (
    <>
      <MobileVersion
        data-blockid="homepage-hero-mobile"
        data-testid="homepage-hero-mobile"
      >
        <BlockContainer as="section" revealOnScroll={false}>
          <Layout gutter={0}>
            <Column size={12}>
              <RelativeBox
                borderRadius="block"
                flex={1}
                bgColor={backgroundColor || 'F4BA42'}
                tabletBgColor={
                  desktopBackgroundColor || backgroundColor || 'F4BA42'
                }
                data-elementid="homepage-hero-mobile-background"
              >
                <CTA
                  backgroundColor="homepageHeroCtaBg"
                  borderRadius="innerBlock"
                  padding={{ 0: 'm', tablet: ['ml', 'l'] }}
                >
                  <CTAText
                    as="h1"
                    fontWeight="h2"
                    colour="defaultText"
                    textAlign="center"
                    data-elementid="homepage-hero-headline"
                    mobileSubheadingSize={mobileSubheadingSize || '1.75'}
                  >
                    {(mobileHeadingText || headingText || '').replace(
                      /<br>/g,
                      ' '
                    )}
                  </CTAText>
                  <Flexbox
                    flexDirection="column"
                    margin={[0, 'auto']}
                    justifyContent="center"
                    alignItems="center"
                  >
                    <HomepagePhoneNumberWrapper data-elementid="phone-wrapper-mobile">
                      <CtaWidget
                        id="homepage-hero-phone-number-mobile"
                        module="Hero"
                        errorPosition="bottom"
                        width="100%"
                        sysId="legacyCta"
                        designType={ctaWidgetEnum.MOBILE_PHONE_NUMBER}
                        placeholder={DEFAULT_LABEL}
                        label={DEFAULT_LABEL}
                        cfButtonColor={
                          (ctaColor as CFColorTokenProp) || 'ginger'
                        }
                        cfButtonHoverColor={
                          (ctaHoverColor as CFColorTokenProp) || 'sweetmint'
                        }
                        buttonContent={ctaText || DEFAULT_BUTTON_TEXT}
                        data-testid="mobilePhoneWidget"
                        data-elementid="phone-widget-mobile"
                      />
                    </HomepagePhoneNumberWrapper>
                    {privacyPolicyDisclaimer && (
                      <Box marginTop="m" marginRight="sm" marginLeft="sm">
                        <RichText
                          data={privacyPolicyDisclaimer}
                          overrideOptions={RICH_TEXT_OVERRIDE}
                        />
                      </Box>
                    )}
                    <PopUp />
                  </Flexbox>
                  <NoSpamText />
                </CTA>
                <ImageContainer>
                  <div style={{ height: '100%', position: 'relative' }}>
                    <MobileImage
                      {...getImageData(image)}
                      loading="eager"
                      heroImage={true}
                      layout="fill"
                      objectFit="cover"
                      width={undefined}
                      height={undefined}
                    />
                  </div>
                </ImageContainer>
              </RelativeBox>
            </Column>
          </Layout>
        </BlockContainer>
        <BlockContainer
          marginTop={{ 0: 'm', tablet: 'l' }}
          marginBottom={eyebrow ? 'm' : { 0: 'm', tablet: '2xl' }}
          revealOnScroll={false}
        >
          <Layout>
            <Column size={{ 0: 11, tablet: 8 }}>
              <Text
                as="h2"
                fontWeight="h2"
                fontSize={{ 0: 'h2_0', tablet: 'h2_desktop' }}
                colour="defaultText"
                textAlign="center"
                data-elementid="homepage-hero-subhead"
              >
                {mobileSubheadingText || subheadingText}
              </Text>
            </Column>
          </Layout>
        </BlockContainer>
        {eyebrow && (
          <BlockContainer
            marginTop="l"
            marginBottom={{ 0: 'm', tablet: 'l' }}
            revealOnScroll={false}
          >
            <Column size={11}>
              <Eyebrow data-testid="eyebrow" eyebrowFields={eyebrow.fields} />
            </Column>
          </BlockContainer>
        )}
      </MobileVersion>

      <DesktopBlockContainer
        marginTop={{ 0: 'blockMarginY', tablet: 'blockMargin' }}
        marginBottom={{ 0: 'blockMarginY', tablet: 'blockMarginY_tablet' }}
        revealOnScroll={false}
        data-blockid="homepage-hero-desktop"
        data-e2e="hero"
      >
        <div style={{ display: 'grid', width: '100%', position: 'relative' }}>
          <div style={{ gridArea: '1/1' }}>
            <BackgroundImage
              {...getImageData(fullWidthDesktopImage as Asset)}
              loading="eager"
              heroImage={true}
            />
          </div>
          <div
            style={{
              gridArea: '1/1',
              position: 'relative',
              placeItems: 'center',
              display: 'grid'
            }}
          >
            <DesktopHeroContents />
          </div>
        </div>
      </DesktopBlockContainer>
    </>
  );
}
